/**
 * Site search JS controller.
 *
 * This class handles the load more and of items on the site search result view.
 */

// Use jQuery as $ within this file scope.
const $ = jQuery;

/**
 * Class SiteSearch. This gets mapped with the keyword SiteSearch and its
 * docready method is fired automatically on pages where there's a
 * class called 'SiteSearch' on the html tag.
 */
class SiteSearch {

    /**
     * The class constructor is for binding class properties.
     */
    constructor() {
    }

    /**
     * Cache dom elements for use in the methods
     */
    cache() {

        this.formInput = $( '#q' );

        // The first caching of all results before hiding anything.
        this.listItems = $( '.js-result-item' );

        // The load more button is shown if there is more items
        // to show than the value of 'this.postsPerPage'.
        this.loadMoreBtn            = $( '.js-load-more' );
        this.loadMoreContainer      = $( '.js-load-more-container' );
        this.loadMoreOriginalOffset = this.loadMoreBtn.attr( 'data-offset' );

        // The pagination setting for the load more functionality.
        // This many items are shown at first and revealed on each load more click.
        this.postsPerPage = 12;

        // The ids related to using more search filters
        this.moreFiltersButtonContainer = $( '#js-more-filters-container' );
        this.moreFiltersButton          = $( '#js-more-filters' );
        this.moreFiltersButtonIcon      = $( '#js-more-filters-icon' );
        this.searchFiltersContainer     = $( '#js-search-filters' );
        this.searchResultsHeading       = $( '#js-result-count' );

        // Gather all the filters to one paragraph for screenreaders
        this.typeFiltersContainer   = $( '#js-search-filters-types' );
        this.targetFiltersContainer = $( '#js-search-filters-targets' );

        this.screenReaderTypes   = $( '#js-sr-search-filters-types' );
        this.screenReaderTargets = $( '#js-sr-search-filters-targets' );

        this.typeFilters   = $( '#js-search-filters-types' ).find( 'input' );
        this.targetFilters = $( '#js-search-filters-targets' ).find( 'input' );

        this.typeFiltersArray   = [];
        this.targetFiltersArray = [];

        this.contentTypeString   = themeData.themeStrings.search.filters.content_type;
        this.contentTargetString = themeData.themeStrings.search.filters.target_group;
    }

    /**
     * All dom events of the class.
     * @return {void}
     */
    events() {

        // Bind a handler to the load more button click event.
        this.loadMoreBtn.on( 'click', e => this.loadMore( e ) );
        this.moreFiltersButton.on( 'click', e => this.toggleMoreFilters( e ) );

        this.typeFilters.each( ( index, item ) => {
            $( item ).on( 'change', ( e ) => this.srFilters( e, item, 'types' ) );
        });

        this.targetFilters.each( ( index, item ) => {
            $( item ).on( 'change', ( e ) => this.srFilters( e, item, 'targets' ) );
        });
    }

    /**
     * This hides the items and shows the first set and a possible load more when javascript is loaded. Otherwise
     * users can see them all and there's no need load more.
     *
     * This also focuses the right elements depending on whether a search query was made or not.
     */
    a11Y() {

        this.listItems.each( ( index, item ) => {
            item.classList.add( 'js-hidden' );
        });

        this.revealSelected( this.postsPerPage );

        this.moreFiltersButtonContainer.removeClass( 'js-hidden' );

        if ( Theme.Common.empty( this.searchResultsHeading ) ) {
            this.formInput.trigger( 'focus' );
        } else {
            this.searchResultsHeading.trigger( 'focus' );
        }

    }

    toggleMoreFilters( e ) {

        // Stop the event with the static method from the Common class.
        Theme.Common.stop( e );

        const ariaExpandedState = this.moreFiltersButton.attr( 'aria-expanded' ) === 'false' ? true : false;
        this.moreFiltersButton.attr( 'aria-expanded', ariaExpandedState );
        this.moreFiltersButtonIcon.toggleClass( 'js-active' );
        this.searchFiltersContainer.toggleClass( 'js-hidden' );
    }

    /**
     * The load more method. First, this method gets the data
     * from the load more -button and calls the 'revealSelected' method with
     * that data. Then it updates the clicked amount / offset of the subsets to
     * be shown with the next load more click.
     *
     * @param {event object} e
     */
    loadMore( e ) {

        // Stop the event with the static method from the Common class.
        Theme.Common.stop( e );

        // The target object of the clicked load more button
        // and the data from its data-attributes.
        const target        = e.target;
        let offset          = +target.dataset.offset;

        // The current set of items to reveal on each click.
        // On the first load the set amount is two.
        // This grows by one after each click and the default
        // item subset amount gets multiplied with it.
        const currentSet    = offset * this.postsPerPage;

        // Call the actual method that reveals the item subset.
        this.revealSelected( currentSet );

        // Grow the offset by one and set it back in to the buttons data in the DOM.
        target.dataset.offset = ++offset;
    }

    /**
     * The method that does the actual revealing of
     * the 'next subset' of items in the load more functionality.
     *
     * @param {int} numToReveal
     */
    revealSelected( numToReveal ) {

        /**
         * First, the currently selected set of items are counted
         * and the active class of the load more button is removed.
         */
        const selectedItems = this.listItems;
        const numOfSelectedItems = selectedItems.length;
        this.loadMoreContainer.removeClass( 'js-active' );

        /**
         * Then, if the amount of items to be revealed (numToReveal)
         * is smaller than the actual set of items, we add the active
         * class back to the button. Otherwise we know that there isn't any more
         * to load. If the amount is larger, we set the amount to the actual amount of the items.
         */
        if ( numToReveal >= numOfSelectedItems ) {
            numToReveal = numOfSelectedItems;
        } else {
            this.loadMoreContainer.addClass( 'js-active' );
        }

        /**
         * Then we slice the amount of items to reveal to a new array and
         * remove the active class of the currently selected set of items.
         *
         * Then we loop through the items in the new array and add an active
         * class to each. This way it looks like we loaded a new subset of items,
         * even though we hid all subsets and then showed a larger subset
         * of items. The subset incrementation happens in the load more method.
         */
        const nextItemsToShow = selectedItems.slice( 0, numToReveal );

        selectedItems.removeClass( 'js-result-active' );

        nextItemsToShow.each( ( index, item ) => {
            item.classList.add( 'js-result-active' );
        });
    }

    /**
     * Function to fill an aria live paragraph
     * @param {event object} e
     * @param {htmlInputElement} item
     * @param {string} container
     */

     srFilters( e, item, container ) {

        // Get the filter
        let label = $( item ).attr( 'id' );

        // If the current checkbox is a 'content type'
        if ( container == 'types' ) {

            // Get the 'for' attribute from the label, to get a translated string and the mark checkbox selected
            label = this.typeFiltersContainer.find( 'label[for =' + label + ']' ).text();
            $( item ).toggleClass( 'selected' );

            // If item is selected, push it to the filters array. If not, splice it.
            if (  $( item ).hasClass( 'selected' ) ) {
                this.typeFiltersArray.push( label );
            } else {
                this.typeFiltersArray.splice( $.inArray( label, this.typeFiltersArray ), 1 );
            }

            // Print the array for screen readers - if its empty, clear it.
            if ( this.typeFiltersArray.length ) {

                // For code readability and debugging: build the filters string for screen readers
                const srFiltersArray = this.contentTypeString + ': ' + this.typeFiltersArray.join( ', ' ) + '. ';
                this.screenReaderTypes.html( srFiltersArray );

            } else {

                // Clear the whole section if the array is empty
                this.screenReaderTypes.html( '' );
            }

        // If the current checkbox is a 'target group'
        } else {

            // Get the 'for' attribute from the label, to get a translated string & mark checkbox selected
            label = this.targetFiltersContainer.find( 'label[for =' + label + ']' ).text();
            $( item ).toggleClass( 'selected' );

            // If item is selected, push it to the filters array. If not, splice it.
            if (  $( item ).hasClass( 'selected' ) ) {
                this.targetFiltersArray.push( label );
            } else {
                this.targetFiltersArray.splice( $.inArray( label, this.targetFiltersArray ), 1 );
            }

            // Print the array for screen readers - if its empty, clear it.
            if ( this.targetFiltersArray.length ) {

                // For code readability and debugging: build the filters string for screen readers
                const srFiltersArray = this.contentTargetString + ': ' + this.targetFiltersArray.join( ', ' ) + '.';

                this.screenReaderTargets.html( srFiltersArray );

            } else {

                // Clear the whole section if the array is empty
                this.screenReaderTargets.html( '' );
            }

        }

     }

    /**
     * Run when the document is ready.
     */
    docReady() {

        this.cache();
        this.a11Y();
        this.events();
    }

}

// Export the class reference.
module.exports = SiteSearch;



// WEBPACK FOOTER //
// ./assets/scripts/sitesearch.js